import { useCallback } from 'react'

import { useLocalStorage } from '@buffer-mono/popcorn'

const DISMISSAL_THRESHOLD = 10

type ChannelDismissalState = {
  dismissalsInRow: number
  shouldShowCoachmark: boolean
  hasSeenCoachmark: boolean
}

type DismissalTrackerState = {
  [channelId: string]: ChannelDismissalState
}

/**
 * Hook for tracking comment dismissal patterns to show bulk actions coachmark.
 *
 * Monitors user behavior to show a helpful coachmark suggesting bulk comment resolution
 * when the user dismisses 10+ comments in a row without replying to any.
 *
 * @param channelIds - Array of channel IDs. Uses the first channelId for tracking.
 * Each channel maintains separate state. Dismissal tracking is user-level behavior,
 * not channel-specific.
 *
 * @returns Object containing:
 * - `dismissalsInRow`: Current count of consecutive dismissals without replies
 * - `shouldShowCoachmark`: Whether to show the bulk actions coachmark
 * - `onDismiss`: Call when a comment is dismissed/resolved to increment the counter
 * - `onReply`: Call when user replies to reset the dismissal streak
 * - `onDismissCoachmark`: Call when user dismisses the coachmark to mark it as seen
 *
 * @example
 * ```tsx
 * const {
 *   dismissalsInRow,
 *   shouldShowCoachmark,
 *   onDismiss,
 *   onReply,
 *   onDismissCoachmark
 * } = useDismissalTracker([channelId])
 *
 * // Track when user dismisses a comment
 * const handleDismiss = () => {
 *   dismissComment(commentId)
 *   onDismiss() // Increment dismissal counter
 * }
 *
 * // Track when user replies to reset streak
 * const handleReply = () => {
 *   replyToComment(comment, text)
 *   onReply() // Reset dismissal counter
 * }
 *
 * // Show coachmark when threshold reached
 * {shouldShowCoachmark && (
 *   <BulkActionsCoachmark onDismiss={onDismissCoachmark} />
 * )}
 * ```
 */
export const useDismissalTracker = (
  channelIds: string[],
): {
  dismissalsInRow: number
  shouldShowCoachmark: boolean
  onDismiss: () => void
  onReply: () => void
  onDismissCoachmark: () => void
} => {
  const [state, setState] = useLocalStorage<DismissalTrackerState>(
    'comment-dismissal-streak',
    {},
  )

  // Use first channelId for tracking (dismissal tracking is user-level behavior)
  // TODO: support multiple channelIds for dismissal tracking
  const channelId = channelIds[0]

  // Get current channel state or default
  const currentChannelState = channelId ? state[channelId] : null
  const dismissalsInRow = currentChannelState?.dismissalsInRow ?? 0
  const shouldShowCoachmark = currentChannelState?.shouldShowCoachmark ?? false

  const onDismiss = useCallback(() => {
    if (!channelId) return

    setState((prev) => {
      const currentChannel = prev[channelId] || {
        dismissalsInRow: 0,
        shouldShowCoachmark: false,
        hasSeenCoachmark: false,
      }

      const newDismissalsInRow = currentChannel.dismissalsInRow + 1
      const nextShouldShowCoachmark =
        newDismissalsInRow >= DISMISSAL_THRESHOLD &&
        !currentChannel.hasSeenCoachmark

      return {
        ...prev,
        [channelId]: {
          ...currentChannel,
          dismissalsInRow: newDismissalsInRow,
          shouldShowCoachmark: nextShouldShowCoachmark,
        },
      }
    })
  }, [setState, channelId])

  const onReply = useCallback(() => {
    if (!channelId) return

    setState((prev) => ({
      ...prev,
      [channelId]: {
        ...(prev[channelId] || {
          dismissalsInRow: 0,
          shouldShowCoachmark: false,
          hasSeenCoachmark: false,
        }),
        dismissalsInRow: 0, // Reset counter when user replies
        shouldShowCoachmark: false,
      },
    }))
  }, [setState, channelId])

  const onDismissCoachmark = useCallback(() => {
    if (!channelId) return

    setState((prev) => ({
      ...prev,
      [channelId]: {
        ...(prev[channelId] || {
          dismissalsInRow: 0,
          shouldShowCoachmark: false,
          hasSeenCoachmark: false,
        }),
        shouldShowCoachmark: false,
        hasSeenCoachmark: true,
      },
    }))
  }, [setState, channelId])

  return {
    dismissalsInRow,
    shouldShowCoachmark,
    onDismiss,
    onReply,
    onDismissCoachmark,
  }
}
